import { useTranslation } from 'react-i18next';
import { Sparkles, Wrench, Crown, Settings, Lock, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { FeatureStatusKind } from '@/constants/featureRegistry';

interface FeatureStatusProps {
  kind: FeatureStatusKind;
  /** Identifier for telemetry, e.g. 'crm.opportunities'. */
  feature?: string;
  /** ISO date or relative label, interpolated as `{{eta}}` in scheduled copy. */
  eta?: string;
  /** Primary CTA: either an action or a link. */
  cta?: { label: string; onClick: () => void } | { label: string; href: string };
  /** Visual size: full page, section card, or inline badge. */
  variant?: 'page' | 'section' | 'inline';
  /** Override the default copy if the page wants a custom phrasing. */
  title?: string;
  description?: string;
}

const ICON_BY_KIND: Record<FeatureStatusKind, typeof Sparkles> = {
  coming_soon:      Sparkles,
  maintenance:      Wrench,
  upgrade_required: Crown,
  not_configured:   Settings,
  beta_unavailable: Lock,
  partial_data:     Clock,
};

/**
 * Single component that renders any "feature unavailable" state.
 *
 * Severity-driven styling: never red. Coming-soon and upgrade-required render
 * neutrally (info), maintenance and not-configured render as warning, beta
 * stays sober. The user should leave thinking "not for me yet" — never
 * "something is broken".
 */
export function FeatureStatus({
  kind,
  feature,
  eta,
  cta,
  variant = 'section',
  title: titleOverride,
  description: descriptionOverride,
}: FeatureStatusProps) {
  const { t } = useTranslation();
  const Icon = ICON_BY_KIND[kind];

  const title = titleOverride ?? (t(`errors.feature.${kind}.title`) as unknown as string);
  const description = descriptionOverride ?? (t(`errors.feature.${kind}.description`, { eta }) as unknown as string);

  if (variant === 'inline') {
    return (
      <Badge variant="secondary" className="gap-1">
        <Icon className="h-3 w-3" aria-hidden="true" />
        {title}
      </Badge>
    );
  }

  const ctaButton = cta ? (
    'href' in cta ? (
      <Button asChild>
        <a href={cta.href}>{cta.label}</a>
      </Button>
    ) : (
      <Button onClick={cta.onClick}>{cta.label}</Button>
    )
  ) : null;

  const containerClass = variant === 'page'
    ? 'flex items-center justify-center px-4 py-16'
    : 'px-4 py-8';

  return (
    <div className={containerClass} data-feature-id={feature} data-feature-status={kind}>
      <Card className="max-w-xl w-full mx-auto border-muted">
        <CardContent className="flex flex-col items-center text-center gap-4 py-10">
          <div className="rounded-full bg-muted p-4">
            <Icon className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
          </div>
          <h2 className="text-xl font-semibold">{title}</h2>
          <p className="text-muted-foreground max-w-md">{description}</p>
          {ctaButton ? <div className="pt-2">{ctaButton}</div> : null}
        </CardContent>
      </Card>
    </div>
  );
}

export default FeatureStatus;
